import { motion } from 'framer-motion';
import { X, Ruler, BedDouble, Wind, ArrowUp } from 'lucide-react';
import type { ApartmentType } from '@pullman/shared';
import { useTranslation } from '@/i18n';

function formatAMD(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatUSD(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

interface PropertyCardExpandedProps {
  property: ApartmentType;
  onCollapse: () => void;
  onAskMore?: (text: string) => void;
}

export function PropertyCardExpanded({ property, onCollapse, onAskMore }: PropertyCardExpandedProps) {
  const { t } = useTranslation();

  const bedroomLabel =
    property.bedrooms.min === property.bedrooms.max
      ? `${property.bedrooms.min}`
      : `${property.bedrooms.min}-${property.bedrooms.max}`;

  return (
    <motion.div
      className="bg-white rounded-card shadow-2xl overflow-hidden border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="font-display text-base tracking-wide text-brand-charcoal">
          {property.name} — {bedroomLabel} {t('property.bedrooms')}
        </h3>
        <button
          onClick={onCollapse}
          className="p-1.5 text-brand-charcoal/40 hover:text-brand-charcoal transition-colors"
          aria-label="Collapse card"
        >
          <X size={18} />
        </button>
      </div>

      {/* Floor plan */}
      <div className="bg-brand-cream/30 p-4">
        <img
          src={property.floor_plan_url}
          alt={`${property.name} floor plan`}
          className="w-full max-h-[300px] object-contain"
          loading="lazy"
        />
      </div>

      {/* Specs grid */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Ruler size={16} className="text-brand-gold" />
            <div>
              <p className="text-brand-charcoal/50 text-xs">{t('property.area')}</p>
              <p className="font-medium">{property.total_area_sqm} {t('property.sqm')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <BedDouble size={16} className="text-brand-gold" />
            <div>
              <p className="text-brand-charcoal/50 text-xs">{t('property.bedrooms')}</p>
              <p className="font-medium">{bedroomLabel}</p>
            </div>
          </div>
          {property.balcony_area_sqm > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Wind size={16} className="text-brand-gold" />
              <div>
                <p className="text-brand-charcoal/50 text-xs">{t('property.balcony')}</p>
                <p className="font-medium">{property.balcony_area_sqm} {t('property.sqm')}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <ArrowUp size={16} className="text-brand-gold" />
            <div>
              <p className="text-brand-charcoal/50 text-xs">{t('property.ceiling')}</p>
              <p className="font-medium">{property.ceiling_height_m}m</p>
            </div>
          </div>
        </div>

        {/* Room breakdown */}
        {property.rooms.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal/50 mb-2">
              {t('property.roomBreakdown')}
            </p>
            <div className="space-y-1">
              {property.rooms.map((room) => (
                <div key={room.name} className="flex justify-between text-sm">
                  <span className="text-brand-charcoal/70">{room.name}</span>
                  <span className="font-medium">{room.area_sqm} {t('property.sqm')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing tiers */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal/50 mb-2">
            {t('property.pricingByFloor')}
          </p>
          <div className="space-y-2">
            {property.pricing.map((tier) => (
              <div key={tier.floor_range} className="flex justify-between items-center text-sm">
                <span className="text-brand-charcoal/70">{tier.floor_range}</span>
                <div className="text-right">
                  <p className="font-semibold text-brand-gold">{formatAMD(tier.price_amd)} AMD</p>
                  <p className="text-xs text-brand-charcoal/50">~{formatUSD(tier.price_usd_approx)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="flex flex-wrap gap-2">
          {property.features.map((feature) => (
            <span
              key={feature}
              className="px-3 py-1 text-xs rounded-full bg-brand-cream text-brand-charcoal/70"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Ask more */}
        <button
          onClick={() => onAskMore?.(`${t('property.askMoreAbout')} ${property.name}`)}
          className="w-full py-2.5 text-sm rounded-full bg-brand-navy text-white
            hover:bg-brand-charcoal transition-all"
        >
          {t('property.askMore')}
        </button>
      </div>
    </motion.div>
  );
}
